import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get("reference");

  const [state, setState] = useState({ status: "verifying", booking: null });

  useEffect(() => {
    if (!reference) {
      setState({ status: "error", booking: null });
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/payment/verify?reference=${reference}`);
        const data = await res.json();
        if (res.ok) {
          setState({ status: data.status === "confirmed" ? "success" : "pending", booking: data });
        } else {
          setState({ status: "error", booking: null });
        }
      } catch {
        setState({ status: "error", booking: null });
      }
    };

    verify();
  }, [reference]);

  if (state.status === "verifying") {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
        <CircularProgress />
        <Typography color="text.secondary">Verifying your payment…</Typography>
      </Box>
    );
  }

  if (state.status === "error") {
    return (
      <Box display="flex" justifyContent="center" mt={8} px={2}>
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ maxWidth: 500, width: "100%" }}>
          Payment verification failed. Please contact support with your reference: <strong>{reference}</strong>
        </Alert>
      </Box>
    );
  }

  const { booking } = state;

  return (
    <Box display="flex" justifyContent="center" mt={8} px={2}>
      <Paper
        elevation={3}
        sx={{ maxWidth: 480, width: "100%", p: { xs: 3, sm: 4 }, borderRadius: 3, textAlign: "center" }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main", mb: 1 }} />

        <Typography variant="h5" fontWeight={700} gutterBottom>
          Booking Confirmed!
        </Typography>
        <Typography color="text.secondary" mb={3}>
          Your payment was received. A confirmation email has been sent to you.
        </Typography>

        {booking && (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <Box textAlign="left" sx={{ "& > *": { mb: 1.5 } }}>
              <Row label="Tour" value={booking.tourName} />
              <Row label="Travelers" value={booking.travelers} />
              <Row
                label="Total Paid"
                value={`NGN ${Number(booking.totalAmount).toLocaleString()}`}
              />
              <Row label="Reference" value={reference} mono />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

const Row = ({ label, value, mono }) => (
  <Box display="flex" justifyContent="space-between" gap={2}>
    <Typography color="text.secondary" fontSize="0.9rem">
      {label}
    </Typography>
    <Typography
      fontWeight={600}
      fontSize="0.9rem"
      sx={mono ? { fontFamily: "monospace", wordBreak: "break-all" } : {}}
    >
      {value}
    </Typography>
  </Box>
);

export default PaymentSuccess;
